import React, { useEffect, useState } from "react";

// Link
import { Link } from "react-router-dom";

// Components
import Button from "components/UiKit/Button";
import FieldText from "components/UiKit/FieldText";
import FieldSelect from "components/UiKit/FieldSelect";

// Constants
import { selectStyle } from "constans/react-select-style";

// Type initial state
type TinitialState = {
  dmso?: string,
  ddH20?: string | number,
  peg300?: string,
  dosage?: string,
  weight?: string,
  tween80?: string,
  cornOil?: string,
  solvent?: string,
  quantity?: string,
  cornOilDMSO?: string,
  dosingVolume?: string,
};
type TinitialStateResult = {
  showCornOil?: boolean,
  outputDMSOML?: string | number,
  outputDosage?: string | number,
  invivoDMSO2?: string | number,
  invivoDMSO?: string | number,
  invivoddH2O?: string | number,
  invivoPEG300?: string | number,
  outputDMSOVol?: string | number,
  invivoCornOil?: string | number,
  invivoTween80?: string | number,
  outputConcentration?: string | number,
};
type TCheckInput = {
  val?: number,
  text?: string,
};

const options = [
  { label: "PEG300", value: "PEG300" },
  { label: "Corn oil", value: "Corn oil" }
];
const mantissa = 4;
const initialStateResult = {
  showCornOil: false,
  outputDMSOVol: "",
  invivoCornOil: "",
  invivoDMSO2: "",
  invivoDMSO: "",
  invivoddH2O: "",
  invivoPEG300: "",
  outputDMSOML: "",
  outputDosage: "",
  invivoTween80: "",
  outputConcentration: ""
};
const initialState = {
  dmso: "",
  peg300: "",
  ddH20: "",
  dosage: "",
  weight: "",
  tween80: "",
  cornOil: "",
  quantity: "",
  cornOilDMSO: "",
  dosingVolume: "",
  solvent: "PEG300"
};

export const FormulationCalculator: React.FC = () => {
  const [error, setError] = useState<string>("");
  const [formValues, setFormValues] = useState<TinitialState>(initialState);
  const [results, setResults] = useState<TinitialStateResult>(initialStateResult);
  const [showResults, setShowResults] = useState<boolean>(false);

  const checkValue = ({ val, text }: TCheckInput) => {
    if ((val || "") < 0) {
      setError(text || ""); // Set the specific error message
      return false;
    }
    setError(""); // Clear the error if value is valid
    return true;
  };

  useEffect(() => {
    const cornOil = Number(formValues?.cornOilDMSO || 0);

    if (!checkValue({ val: cornOil, text: "Please enter the correct ratio." })) return;

    const cornOilData = (100 - cornOil).toFixed(1);
    const parsedCornOilData = parseFloat(cornOilData);

    if (!checkValue({ val: parsedCornOilData, text: "Please enter the correct ratio." })) return;

    setFormValues({
      ...formValues,
      cornOil: "" + (parsedCornOilData < 0 ? 0 : parsedCornOilData)
    });
  }, [formValues.cornOilDMSO]);

  useEffect(() => {
    const dmsoValue = Number(formValues?.dmso || 0);
    const peg300Value = Number(formValues?.peg300 || 0);
    const tween80Value = Number(formValues?.tween80 || 0);

    if ([dmsoValue, peg300Value, tween80Value].some(val => !checkValue({
      val,
      text: "Please enter the correct ratio."
    }))) return;

    const ddH20 = (100 - (dmsoValue + peg300Value + tween80Value)).toFixed(1);
    const parsedDdH20 = parseFloat(ddH20);

    if (!checkValue({ val: parsedDdH20, text: "Please enter the correct ratio." })) return;

    setFormValues((prev) => ({
      ...prev,
      ddH20: parsedDdH20 < 0 ? 0 : parsedDdH20
    }));
  }, [formValues?.dmso, formValues?.peg300, formValues?.tween80]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (Number(value) < 0) {
      return;
    }

    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const calculateFormulation = () => {
    let formPEG300 = {};
    let formDataCornOil = {};

    const dosage: number = +(formValues?.dosage || "");
    const weight: number = +(formValues?.weight || "");
    const quantity: number = +(formValues?.quantity || "");
    const dosingVolume: number = +(formValues?.dosingVolume || "");

    if (
      !dosage ||
      !weight ||
      !quantity ||
      !dosingVolume
    ) {
      setError("Please fill in all fields.");
      return;
    } else setError("");

    if (!results?.showCornOil) {
      if (!+(formValues.dmso || "") ||
        !+(formValues.ddH20 || "") ||
        !+(formValues.peg300 || "") ||
        !+(formValues.tween80 || "")
      ) {
        setError("Please fill in all fields.");
        return;
      } else setError("");
    } else {
      if (!formValues.cornOil || !formValues.cornOilDMSO) {
        setError("Please fill in all fields.");
        return;
      } else setError("");
    }

    let dmso = +(formValues.dmso || "") / 100;

    if (formValues.solvent !== "PEG300") {
      dmso = +(formValues.cornOilDMSO || "") / 100;
    }

    const peg300 = +(formValues?.peg300 || "") / 100;
    const tween80 = +(formValues?.tween80 || "") / 100;
    const ddh20 = 1 - dmso - peg300 - tween80;
    const outputConcentration = dosage * weight / dosingVolume;
    const outputDMSOVol = quantity * dosingVolume * dmso;
    const outputDMSOML = outputConcentration / dmso;
    const outputDosage = outputDMSOVol * outputDMSOML / 1000;

    if (formValues.solvent !== "PEG300") {
      const cornOilDmso = +(formValues.cornOilDMSO || "") / 100;
      const cornOil = +(formValues.cornOil || "") / 100;
      const outputDMSOVol = quantity * dosingVolume * cornOilDmso;
      const invivoCornOil = quantity * dosingVolume * cornOil;
      formDataCornOil = {
        invivoDMSO2: parseFloat(outputDMSOVol.toFixed(mantissa)),
        invivoCornOil: parseFloat(invivoCornOil.toFixed(mantissa))
      };
    }

    if (formValues.solvent === "PEG300") {
      const invivoDMSO = outputDMSOVol;
      const invivoPEG300 = quantity * dosingVolume * peg300;
      const invivoTween80 = quantity * dosingVolume * tween80;
      const invivoddH2O = dosingVolume * quantity * ddh20;

      formPEG300 = {
        invivoDMSO: parseFloat(invivoDMSO.toFixed(mantissa)),
        invivoddH2O: parseFloat(invivoddH2O.toFixed(mantissa)),
        invivoPEG300: parseFloat(invivoPEG300.toFixed(mantissa)),
        invivoTween80: parseFloat(invivoTween80.toFixed(mantissa))
      };
    }

    setResults({
      ...results,
      ...formPEG300,
      ...formDataCornOil,
      outputDosage: parseFloat(outputDosage.toFixed(mantissa)),
      outputDMSOML: parseFloat(outputDMSOML.toFixed(mantissa)),
      outputDMSOVol: parseFloat(outputDMSOVol.toFixed(mantissa)),
      outputConcentration: parseFloat(outputConcentration.toFixed(mantissa))
    });

    setShowResults(true); // Show results after calculation
  };

  const handleReset = () => {
    setFormValues(initialState);
  };

  const handleSolventChange = (opt: { label: string, value: string }) => {
    const value = opt.value;
    setFormValues({
      ...formValues,
      solvent: value
    });
    setShowResults(false);
    setResults({
      ...initialStateResult,
      showCornOil: value === "Corn oil"
    });
  };

  const styleSelectCalculator = {
    ...selectStyle,
    singleValue: (p: any) => ({
      ...p,
      fontWeight: "500",
      fontSize: "1.2rem",
      fontStyle: "normal",
      borderStyle: "unset",
      lineHeight: "1.2rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins"
    }),
    container: (provided: any) => ({
      ...provided,
      "&:hover, &:focus": {},
      height: "4rem",
      width: "9rem",
      color: "#060606",
      maxWidth: "100%",
      fontWeight: "500",
      fontSize: "1.4rem",
      fontStyle: "normal",
      lineHeight: "2.4rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins",

      "@media only screen and (max-width: 1024px)": {
        ...provided["@media only screen and (max-width: 1024px)"],
        maxWidth: "100%"
      }
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9B9B9B",
      fontWeight: "500",
      fontSize: "1.4rem",
      fontStyle: "normal",
      lineHeight: "2.4rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins"
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 999,
      "&:hover, &:focus": {}
    }),
    control: (provided: any) => ({
      ...provided,
      "&:hover, &:focus": {
        borderColor: "unset"
      },
      height: "4rem",
      border: "unset",
      width: "9rem",
      color: "#060606",
      boxShadow: "none",
      maxWidth: "100%",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "1.3rem",
      fontStyle: "normal",
      borderStyle: "unset",
      lineHeight: "1.2rem",
      borderRadius: "unset",
      letterSpacing: "0.8px",
      fontFamily: "Poppins",
      backgroundColor: "transparent",
      borderBottom: "1px solid rgba(6, 6, 6, 0.25)",

      "@media only screen and (max-width: 1024px)": {
        ...provided["@media only screen and (max-width: 1024px)"],
        maxWidth: "100%"
      }
    })
  };

  return (
    <div className="formulation-calculator">
      <p className="formulation-calculator__text mt20 mb20">
        Step 1: Enter information below (Recommended: An additional animal making an allowance for loss during the
        experiment)
      </p>
      <div className="flex flex-ai-end">
        <FieldText
          name="dosage"
          value={formValues.dosage}
          label="Dosage: mg/kg"
          innerTypeInput="number"
          placeholder="Your value"
          onChange={handleInputChange}
          className="formulation-calculator__input mr10"
        />
        <span className="formulation-calculator__sign mr10">&#8594;</span>
        <FieldText
          name="weight"
          value={formValues.weight}
          innerTypeInput="number"
          placeholder="Your value"
          onChange={handleInputChange}
          label="Average weight of animals: g"
          className="formulation-calculator__input mr10"
        />
        <span className="formulation-calculator__sign mr10">&#8594;</span>
        <FieldText
          name="dosingVolume"
          innerTypeInput="number"
          placeholder="Your value"
          value={formValues.dosingVolume}
          onChange={handleInputChange}
          label="Dosing volume per animal: μL"
          className="formulation-calculator__input mr10"
        />
        <span className="formulation-calculator__sign mr10">&#8594;</span>
        <FieldText
          name="quantity"
          innerTypeInput="number"
          value={formValues.quantity}
          placeholder="Your value"
          label="Number of animals:"
          onChange={handleInputChange}
          className="formulation-calculator__input"
        />
      </div>

      <p className="formulation-calculator__text mt20 mb20">
        Step 2: Enter the in vivo formulation (<Link to="/contact-us">
        This is only the calculator, not formulation. Please contact us first if there is no in vivo formulation at the
        solubility Section.
      </Link>)
      </p>

      {formValues.solvent === "PEG300" ? (
        <div className="flex flex-ai-end">
          <FieldText
            name="dmso"
            label="%DMSO"
            value={formValues.dmso}
            innerTypeInput="number"
            placeholder="Your value"
            onChange={handleInputChange}
            className="formulation-calculator__input"
          />
          <span className="formulation-calculator__sign mr10 ml10">+</span>
          <FieldText
            name="peg300"
            innerTypeInput="number"
            placeholder="Your value"
            value={formValues.peg300}
            onChange={handleInputChange}
            className="formulation-calculator__input"
          />
          <span className="formulation-calculator__sign mr10 ml10">%</span>
          <FieldSelect
            name="solvent"
            options={options}
            placeholder="Select"
            styles={styleSelectCalculator}
            onChange={handleSolventChange}
            value={options?.find(item => item.value === formValues.solvent)}
          />
          <span className="formulation-calculator__sign mr10 ml10">+</span>
          <FieldText
            name="tween80"
            label="Tween 80"
            innerTypeInput="number"
            placeholder="Your value"
            value={formValues.tween80}
            onChange={handleInputChange}
            className="formulation-calculator__input"
          />
          <span className="formulation-calculator__sign mr10 ml10">+</span>
          <FieldText
            name="ddH20"
            onChange={() => {
            }}
            value={formValues.ddH20}
            placeholder="Your value"
            label={`ddH<sub>2</sub>O`}
            className="formulation-calculator__input"
          />
        </div>
      ) : (
        <div id="cornOilTd" className="flex flex-ai-end">
          <FieldText
            label="%DMSO"
            name="cornOilDMSO"
            innerTypeInput="number"
            placeholder="Your value"
            onChange={handleInputChange}
            value={formValues.cornOilDMSO}
            className="formulation-calculator__input mr5"
          />
          <span className="formulation-calculator__sign mr5">+</span>

          <FieldText
            name="cornOil"
            innerTypeInput="number"
            placeholder="Your value"
            value={formValues.cornOil}
            onChange={handleInputChange}
            className="formulation-calculator__input mr5"
          />
          <span className="formulation-calculator__sign mr5">%</span>
          <FieldSelect
            name="solvent"
            options={options}
            placeholder="Select"
            styles={styleSelectCalculator}
            onChange={handleSolventChange}
            value={options?.find(item => item.value === formValues.solvent)}
          />
        </div>
      )}

      <div className="mt20 mb20">
        <Button
          color="green"
          label="Calculate"
          disabled={!!error}
          variable="primary"
          onPress={calculateFormulation}
          className="formulation-calculator__submit mb10 mr10"
        />
        <Button
          label="Reset"
          variable="outline"
          onPress={handleReset}
          className="formulation-calculator__submit-clear"
        />
      </div>

      {error && <p className="formulation-calculator__error-text mt10 mb10">{error}</p>}

      {showResults && (
        <div className="invivo-formulation-output">
          <hr className="dashed" />
          <p className="formulation-calculator__text mt20">Calculation results:</p>

          <div className="flex flex-ai-end">
            <p className="formulation-calculator__text-result"><b>Working concentration:</b></p>
            <FieldText
              onChange={() => {
              }}
              name="outputConcentration"
              value={results.outputConcentration}
              className="formulation-calculator__input-view mr5 ml5 mt10"
            />
            <span className="formulation-calculator__text-result">mg/ml;</span>
          </div>
          <div className="mt20">
            <div className="flex flex-ai-end mb20">
              <p className="formulation-calculator__text-result"><b>Method for preparing DMSO master liquid:</b></p>
              <FieldText
                onChange={() => {
                }}
                name="outputDosage"
                value={results?.outputDosage || ""}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">mg drug pre-dissolved in</p>
              <FieldText
                onChange={() => {
                }}
                name="outputDMSOVol"
                value={results.outputDMSOVol}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL DMSO ( Master liquid concentration</p>
              <FieldText
                onChange={() => {
                }}
                name="outputDMSOVol"
                value={results?.outputDMSOML}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">mg/mL,</p>
            </div>
            <Link to="/contact-us" className="formulation-calculator__text">
              Please contact us first if the concentration exceeds the DMSO solubility of the batch of drug.
            </Link>{" "}
            )
          </div>

          {!results.showCornOil ? (
            <div className="flex flex-ai-end flex-jc-start mt20">
              <p className="formulation-calculator__text-result"><b>Method for preparing in vivo formulation:</b></p>
              <p className="formulation-calculator__text-result">Take</p>
              <FieldText
                name="invivoDMSO"
                onChange={() => {
                }}
                value={results.invivoDMSO}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL DMSO master liquid, next add</p>
              <FieldText
                name="invivoPEG300"
                onChange={() => {
                }}
                value={results.invivoPEG300}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL PEG300, mix and clarify, next add</p>
              <FieldText
                name="invivoTween80"
                onChange={() => {
                }}
                value={results.invivoTween80}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL Tween 80, mix and clarify, next add</p>
              <FieldText
                name="invivoddH2O"
                onChange={() => {
                }}
                value={results.invivoddH2O}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL ddH<sub>2</sub>O, mix and clarify</p>
            </div>
          ) : (
            <div className="flex flex-ai-end flex-jc-start mt20">
              <p className="formulation-calculator__text-result"><b>Method for preparing in vivo formulation:</b></p>
              <p className="formulation-calculator__text-result">Take</p>
              <FieldText
                name="invivoDMSO2"
                onChange={() => {
                }}
                value={results.invivoDMSO2}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL DMSO master liquid, next add</p>
              <FieldText
                onChange={() => {
                }}
                name="invivoCornOil"
                value={results.invivoCornOil}
                className="formulation-calculator__input-view mr5 ml5"
              />
              <p className="formulation-calculator__text-result">μL Corn oil, mix and clarify.</p>
            </div>
          )}

          <div className="mt20">
            <p className="formulation-calculator__text-result">Note: </p>
            <ul>
              <li className="formulation-calculator__text-result">Please make sure the liquid is clear before adding the
                next solvent.
              </li>
              <li className="formulation-calculator__text-result">Be sure to add the solvent(s) in order. You must
                ensure that the solution obtained, in the previous addition, is a
                clear solution before proceeding to add the next solvent. Physical methods such
              </li>
            </ul>
            <p className="formulation-calculator__text-result">
              as vortex, ultrasound or hot water bath can be used to aid dissolving.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulationCalculator;